import { useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { MessageCircle, RotateCcw, RotateCw, Hand } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { waLink } from "../lib/site";
import { REAL_SENSOR_IMG } from "../three/HeroScene";

const PARTS = [
  {
    id: "body",
    name: "جسم الحساس",
    short: "الهيكل الخارجي",
    pos: { left: "26%", top: "48%" },
    info: "يحتوي جسم الحساس على الدائرة الإلكترونية وهو مصمم لتحمل الاهتزاز والحرارة داخل الإطار، ويختلف شكله وحجمه حسب الشركة الصانعة ونوع الحساس.",
  },
  {
    id: "valve",
    name: "صمام الهواء",
    short: "Valve Stem",
    pos: { left: "70%", top: "45%" },
    info: "يمرّر الهواء إلى الإطار ويثبّت الحساس في الجنط. تتوفر صمامات معدنية (ألمنيوم) ومطاطية، ويُنصح بفحصها أو استبدالها عند تغيير الحساس لتجنب التسريب.",
  },
  {
    id: "diaphragm",
    name: "وحدة قياس الضغط",
    short: "Pressure Unit",
    pos: { left: "38%", top: "64%" },
    info: "غشاء قياس دقيق يلتقط ضغط الهواء الفعلي داخل الإطار ويحوّله إلى قراءة رقمية. بعض الحساسات تقيس أيضًا درجة الحرارة ضمن الوحدة نفسها، حسب النوع.",
  },
  {
    id: "chip",
    name: "وحدة قياس الحرارة والإرسال",
    short: "RF Chip",
    pos: { left: "28%", top: "30%" },
    info: "رقاقة إلكترونية تعالج القراءات وترسلها لاسلكيًا إلى وحدة السيارة بتردد شائع مثل 433MHz أو 315MHz حسب السيارة. دعم قياس الحرارة يختلف من حساس لآخر.",
  },
  {
    id: "battery",
    name: "البطارية الداخلية",
    short: "Battery",
    pos: { left: "17%", top: "62%" },
    info: "بطارية ليثيوم مدمجة غير قابلة للشحن، يتراوح عمرها الافتراضي غالبًا بين 5 إلى 7 سنوات حسب الاستخدام والنوع، وعند نفادها يُستبدل الحساس كاملًا في أغلب الأنواع.",
  },
  {
    id: "mounting",
    name: "طريقة التركيب داخل الإطار",
    short: "Mounting",
    pos: null,
    info: "يُركّب الحساس داخل الجنط مكان صمام الهواء بعد فك الإطار جزئيًا، ثم يُعاد ترصين الإطار وتعبئته وبرمجة الحساس على وحدة السيارة لتظهر القراءات بشكل صحيح.",
  },
];

const RealSensorTilt = ({ active, onSelect, reduced }) => {
  const ref = useRef(null);
  const frame = useRef(null);
  const dragging = useRef(false);

  const applyTilt = (clientX, clientY, strength = 1) => {
    if (reduced || !ref.current || !frame.current) return;
    const rect = ref.current.getBoundingClientRect();
    const px = (clientX - rect.left) / rect.width - 0.5;
    const py = (clientY - rect.top) / rect.height - 0.5;
    frame.current.style.transition = "transform 0.08s linear";
    frame.current.style.transform = `perspective(1000px) rotateY(${px * 26 * strength}deg) rotateX(${py * -16 * strength}deg)`;
  };

  const resetTilt = () => {
    if (!frame.current) return;
    frame.current.style.transition = "transform 0.7s cubic-bezier(0.22,1,0.36,1)";
    frame.current.style.transform = "perspective(1000px) rotateY(0deg) rotateX(0deg)";
  };

  return (
    <div
      ref={ref}
      className="absolute inset-0 flex items-center justify-center touch-pan-y"
      onPointerMove={(e) => {
        if (e.pointerType === "touch") {
          if (dragging.current) applyTilt(e.clientX, e.clientY, 1.4);
        } else {
          applyTilt(e.clientX, e.clientY);
        }
      }}
      onPointerDown={(e) => {
        dragging.current = true;
        applyTilt(e.clientX, e.clientY, 1.4);
      }}
      onPointerUp={() => {
        dragging.current = false;
        resetTilt();
      }}
      onPointerLeave={() => {
        dragging.current = false;
        resetTilt();
      }}
    >
      <div
        ref={frame}
        data-testid="sensor-real-tilt-frame"
        className="relative w-[86%] max-w-[440px] rounded-2xl overflow-hidden border-2 border-slate-400/40 bg-white shadow-[0_24px_70px_rgba(0,0,0,0.6),0_0_40px_rgba(230,57,70,0.12)]"
        style={{ transformStyle: "preserve-3d", willChange: "transform" }}
      >
        <img
          src={REAL_SENSOR_IMG}
          alt="حساس ضغط إطارات حقيقي TPMS بتردد 433MHz مع صمام معدني"
          className="w-full h-auto block select-none pointer-events-none"
          draggable="false"
        />
        <div className="absolute bottom-0 inset-x-0 h-1.5 bg-red-600" />
        {PARTS.filter((p) => p.pos).map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`sensor-hotspot-${p.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(p.id);
            }}
            aria-label={`معلومات: ${p.name}`}
            style={{ left: p.pos.left, top: p.pos.top }}
            className={`absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all z-10 ${
              active === p.id
                ? "bg-red-600 border-white text-white scale-110 shadow-[0_0_18px_rgba(230,57,70,0.8)]"
                : "bg-black/70 border-red-500 text-red-400 hotspot-dot"
            }`}
          >
            +
          </button>
        ))}
      </div>
      <span className="absolute top-3 right-3 font-mono text-[10px] tracking-widest text-emerald-400 border border-emerald-500/40 rounded-full px-3 py-1 bg-black/50 backdrop-blur-sm">
        حساس حقيقي · صورة فوتوغرافية
      </span>
      <span className="absolute bottom-3 right-3 hidden sm:flex items-center gap-1.5 font-mono text-[10px] text-slate-500">
        <Hand size={12} />
        حرّك المؤشر أو اسحب للإمالة
      </span>
    </div>
  );
};

export const SensorExplorer = () => {
  const [active, setActive] = useState("valve");
  const [mode, setMode] = useState("interactive");
  const reduce = useReducedMotion();
  const part = PARTS.find((p) => p.id === active);

  const rotateNudge = (dir) => {
    const idx = PARTS.findIndex((p) => p.id === active);
    const next = (idx + dir + PARTS.length) % PARTS.length;
    setActive(PARTS[next].id);
  };

  return (
    <section
      id="tech"
      data-testid="sensor-explorer-section"
      className="relative bg-[#0C0E14] py-20 sm:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 tech-grid-bg opacity-40" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Interactive 3D"
          title="اكتشف تقنية حساس الكفر"
          desc="حساس TPMS حقيقي أمامك — أمِل الصورة بالماوس أو بالسحب، واختر النقاط التفاعلية للتعرف على مكوناته. تختلف التصاميم والمواصفات من حساس لآخر حسب النوع والشركة."
        />
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <Reveal className="relative h-[340px] sm:h-[420px] rounded-3xl border border-white/10 bg-[radial-gradient(ellipse_60%_60%_at_50%_45%,rgba(230,57,70,0.08),transparent),#08090C] overflow-hidden">
            {/* mode toggle */}
            <div className="absolute top-3 left-3 z-20 flex rounded-full border border-white/15 bg-black/50 backdrop-blur-sm overflow-hidden">
              {[
                { id: "interactive", label: "تفاعلي", testId: "sensor-mode-interactive" },
                { id: "cinematic", label: "سينمائي 360°", testId: "sensor-mode-cinematic" },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={m.testId}
                  onClick={() => setMode(m.id)}
                  className={`text-[11px] font-semibold px-3.5 py-1.5 transition-colors ${
                    mode === m.id ? "bg-red-600 text-white" : "text-slate-300 hover:text-white"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {mode === "interactive" ? (
              <RealSensorTilt active={active} onSelect={setActive} reduced={reduce} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <video
                  data-testid="sensor-360-video"
                  poster="/assets/sensor-360-poster.jpg"
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                  autoPlay={!reduce}
                  controls={!!reduce}
                  preload="auto"
                  aria-label="مقطع سينمائي لدوران حساس ضغط إطارات حقيقي 360 درجة"
                >
                  <source src="/assets/sensor-360.mp4" type="video/mp4" />
                  <source src="/assets/sensor-360.webm" type="video/webm" />
                </video>
                <span className="absolute bottom-3 right-3 font-mono text-[10px] tracking-widest text-slate-400 border border-white/15 rounded-full px-3 py-1 bg-black/50 backdrop-blur-sm pointer-events-none">
                  دوران 360° · حساس حقيقي
                </span>
              </div>
            )}

            {mode === "interactive" && (
            <div className="absolute bottom-3 left-3 flex gap-2 z-10">
              <button
                type="button"
                data-testid="sensor-rotate-right-btn"
                onClick={() => rotateNudge(1)}
                aria-label="الجزء التالي"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/15 text-slate-300 flex items-center justify-center hover:border-red-500/50 hover:text-white transition-all"
              >
                <RotateCw size={16} />
              </button>
              <button
                type="button"
                data-testid="sensor-rotate-left-btn"
                onClick={() => rotateNudge(-1)}
                aria-label="الجزء السابق"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/15 text-slate-300 flex items-center justify-center hover:border-red-500/50 hover:text-white transition-all"
              >
                <RotateCcw size={16} />
              </button>
            </div>
            )}
          </Reveal>

          <Reveal delay={0.15}>
            <div className="flex flex-wrap gap-2 mb-6">
              {PARTS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`sensor-part-btn-${p.id}`}
                  onClick={() => setActive(p.id)}
                  className={`text-xs sm:text-sm px-3.5 py-2 rounded-full border transition-all ${
                    active === p.id
                      ? "bg-red-600 border-red-500 text-white font-semibold"
                      : "bg-white/5 border-white/10 text-slate-300 hover:border-red-500/40"
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
            <div
              data-testid="sensor-info-card"
              className="metal-card rounded-2xl p-6 sm:p-8 min-h-[190px]"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-xl font-bold text-white">{part.name}</h3>
                <span className="font-mono text-[10px] tracking-widest text-red-400 uppercase" dir="ltr">
                  {part.short}
                </span>
              </div>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed">{part.info}</p>
            </div>
            <a
              href={waLink("السلام عليكم، أرغب في الاستفسار عن الحساس المعروض في قسم «اكتشف تقنية حساس الكفر».")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sensor-explorer-whatsapp-btn"
              className="mt-6 inline-flex items-center gap-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-6 py-3 rounded-full shadow-[0_4px_20px_rgba(16,185,129,0.35)] transition-all active:scale-[0.97]"
            >
              <MessageCircle size={18} />
              استفسر عن هذا الحساس
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
